import { useCallback, useState } from 'react';
import { ref, push, set, get } from 'firebase/database';
import { db } from '../firebase';
import { Quiz } from '../types/quiz.types';

// Always exactly 6 digits (leading zeros allowed — JoinQuiz keeps them), and
// verified unused: `set()` on a colliding code would silently overwrite a LIVE
// session, killing someone's game mid-stream.
const newSessionCode = async (): Promise<string> => {
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0');
    const existing = await get(ref(db, `sessions/${code}`));
    if (!existing.exists()) return code;
  }
  throw new Error('Could not find a free game PIN — try again.');
};

// Admin authoring helpers. The live runtime (join/play/score) lives in
// services/live-session.ts — keep create-side and run-side concerns separate.
export const useQuiz = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Accepts the authoring schema (options carry isCorrect) and writes two nodes:
  //   quizzes/{id}        - PUBLIC blob: questions as arrays, options as text[],
  //                         NO correct-answer field.
  //   correctAnswers/{id} - the correct option INDEX per question, kept separate
  //                         so it's never shipped to audience clients.
  const createQuiz = useCallback(
    async (quizData: Omit<Quiz, 'id' | 'questions'> & { questions: any[] }) => {
      try {
        setLoading(true);
        const newQuizRef = push(ref(db, 'quizzes'));
        const quizId = newQuizRef.key;
        if (!quizId) throw new Error('Failed to generate quiz ID');

        // Accept boolean true or the string "true"; everything else (incl. the
        // string "false", which is truthy!) is not the answer.
        const isTrue = (v: any) =>
          v === true || (typeof v === 'string' && v.toLowerCase() === 'true');

        const correctAnswers: number[] = [];
        const questions = (quizData.questions ?? []).map((q: any, i: number) => {
          const correctIdx = (q.options ?? []).findIndex((o: any) => isTrue(o.isCorrect));
          if (correctIdx < 0) {
            // Reject up front — a quiz with no marked answer can never be scored,
            // which otherwise shows up as a mysterious all-zero leaderboard.
            throw new Error(
              `Question ${i + 1} ("${(q.questionText || '').slice(0, 40)}…") has no option ` +
                `marked "isCorrect": true. Every question needs exactly one.`
            );
          }
          correctAnswers.push(correctIdx);
          return {
            questionText: q.questionText ?? '',
            questionImage: q.questionImage || '',
            questionVideo: q.questionVideo || '',
            options: (q.options ?? []).map((o: any) => o.optionText ?? ''),
            timer: Number(q.timer) || 30,
          };
        });

        await set(newQuizRef, {
          title: quizData.title,
          createdBy: quizData.createdBy,
          questions,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
        await set(ref(db, `correctAnswers/${quizId}`), correctAnswers);

        return quizId;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create quiz');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Create a session in the lean shape clients will subscribe to. hostUid is
  // the creating host's uid — rules require it to be the caller's own uid, make
  // it immutable, and gate every subsequent host op on it (presenters can only
  // drive sessions they host). A non-empty youtubeUrl switches the session into
  // STREAM MODE (question on the broadcast, phone is a shape-only answer pad);
  // omit it for app mode.
  const createSession = useCallback(async (quizId: string, hostUid: string, youtubeUrl?: string) => {
    try {
      setLoading(true);
      const sessionCode = await newSessionCode();
      const url = youtubeUrl?.trim();
      await set(ref(db, `sessions/${sessionCode}`), {
        quizId,
        hostUid,
        status: 'not_started',
        currentQuestionIndex: 0,
        questionStartTime: 0,
        createdAt: Date.now(),
        ...(url ? { youtubeUrl: url, answersOpen: false } : {}),
      });
      return sessionCode;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin-only (rules reject it for everyone else): put a quiz into / pull it
  // out of the shared catalog every presenter can host but never modify.
  const setQuizCatalog = useCallback(async (quizId: string, inCatalog: boolean) => {
    try {
      await set(ref(db, `quizzes/${quizId}/catalog`), inCatalog ? true : null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update catalog flag');
      return false;
    }
  }, []);

  return { loading, error, createQuiz, createSession, setQuizCatalog };
};
